import React, { useState, useEffect } from 'react';
import { Bot, Plus, Settings, MessageSquare, Trash2, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';

const BotsPage = () => {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: 'Você é um assistente de IA prestativo e amigável.',
    welcome_message: 'Olá! Como posso ajudar você hoje?',
    model: 'gpt-5.2',
    provider: 'openai'
  });

  useEffect(() => {
    fetchBots();
  }, []);

  const fetchBots = async () => {
    try {
      const response = await api.get('/api/bots');
      if (response.ok) {
        const data = await response.json();
        setBots(data);
      }
    } catch (error) {
      console.error('Failed to fetch bots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await api.post('/api/bots', formData);
      if (response.ok) {
        const newBot = await response.json();
        setBots([...bots, newBot]);
        setIsCreateOpen(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to create bot:', error);
    }
  };

  const handleDelete = async (botId) => {
    if (!window.confirm('Tem certeza que deseja excluir este bot?')) return;
    try {
      const response = await api.delete(`/api/bots/${botId}`);
      if (response.ok) {
        setBots(bots.filter(b => b.bot_id !== botId));
      }
    } catch (error) {
      console.error('Failed to delete bot:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      system_prompt: 'Você é um assistente de IA prestativo e amigável.',
      welcome_message: 'Olá! Como posso ajudar você hoje?',
      model: 'gpt-5.2',
      provider: 'openai'
    });
  };

  const models = [
    { value: 'gpt-5.2', label: 'GPT-5.2 (Mais Avançado)', provider: 'openai' },
    { value: 'gpt-4.1', label: 'GPT-4.1', provider: 'openai' },
    { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet', provider: 'anthropic' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', provider: 'gemini' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meus Bots</h1>
          <p className="text-gray-500">Gerencie seus chatbots com IA</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb]">
              <Plus className="h-4 w-4 mr-2" />
              Criar Bot
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Criar Novo Bot</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome do Bot</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Assistente de Vendas"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o propósito do bot"
                />
              </div>
              <div>
                <Label>Modelo de IA</Label>
                <Select
                  value={formData.model}
                  onValueChange={(value) => {
                    const model = models.find(m => m.value === value);
                    setFormData({ ...formData, model: value, provider: model?.provider || 'openai' });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prompt do Sistema</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  placeholder="Defina a personalidade e comportamento do bot"
                  rows={4}
                />
              </div>
              <div>
                <Label>Mensagem de Boas-vindas</Label>
                <Textarea
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  placeholder="Mensagem inicial para os usuários"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} className="bg-[#3b82f6] hover:bg-[#2563eb]">
                  Criar Bot
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">Carregando...</div>
      ) : bots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhum bot criado</h3>
            <p className="text-gray-500 mb-4">Crie seu primeiro bot para começar</p>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-[#3b82f6] hover:bg-[#2563eb]">
              <Plus className="h-4 w-4 mr-2" />
              Criar Bot
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bots.map((bot) => (
            <Card key={bot.bot_id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-lg font-medium">{bot.name}</CardTitle>
                <div className={`w-3 h-3 rounded-full ${bot.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">{bot.description || 'Sem descrição'}</p>
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                  <span className="px-2 py-1 bg-gray-100 rounded">{bot.model}</span>
                  <span className="px-2 py-1 bg-gray-100 rounded">{bot.provider}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setSelectedBot(bot)}
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Testar
                  </Button>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(bot.bot_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Test Bot Dialog */}
      {selectedBot && (
        <BotTestDialog bot={selectedBot} onClose={() => setSelectedBot(null)} />
      )}
    </div>
  );
};

// Bot Test Dialog Component
const BotTestDialog = ({ bot, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => {
    // Add welcome message
    setMessages([{ role: 'assistant', content: bot.welcome_message }]);
  }, [bot]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post(`/api/chat/${bot.bot_id}`, {
        message: input,
        conversation_id: conversationId
      });

      if (response.ok) {
        const data = await response.json();
        setConversationId(data.conversation_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.assistant_message.content }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erro ao processar mensagem.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg h-[600px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-[#3b82f6]" />
            Testar: {bot.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-[#3b82f6] text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 px-4 py-2 rounded-2xl">
                <span className="animate-pulse">Digitando...</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-4 border-t">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Digite sua mensagem..."
            disabled={loading}
          />
          <Button onClick={sendMessage} disabled={loading} className="bg-[#3b82f6] hover:bg-[#2563eb]">
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BotsPage;
